import { Box, Grid, Typography } from '@mui/material';
import { ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import {
  Control,
  UseFormSetValue,
  UseFormTrigger,
  useWatch,
} from 'react-hook-form';
import {
  ProductFamily,
  SpecialPaymentProducts,
} from '../../../../../../../../types/api';
import ShowIf from '../../../../../components/ConditionalRender/ShowIf';
import {
  FOUR_YEAR_TERM_WARNING_MESSAGE,
  SPECIAL_PAYMENT_REQUEST_OPTIONS,
  SPECIAL_PAYMENT_REQUEST_OPTIONS_WITH_20,
  TAX_CREDIT_REQUEST_OPTIONS,
  UNITED_STATES,
} from '../../../../../constants';
import { useAuth } from '../../../../../hooks';
import useFeatureFlag from '../../../../../hooks/useFeatureFlag';
import { useInstallerLoanProducts } from '../../../../../hooks/useInstallerLoanProducts';
import { useLinkedLoanAmounts } from '../../../../../hooks/useLinkedLoanAmounts';
import { Milestone, PrepaymentPenalty } from '../../../../../modules/loan';
import { Installer } from '../../../../../modules/installer';
import { Asset } from '../../../../../modules/loan/asset';
import { ProjectTypes } from '../../../../../modules/loan/projectType';
import WarningMessage from '../../../../../pages/dashboard/loan_detail/flows/NewWelcomeFlow/components/WarningMessage';
import { RAILWAY_FEATURE_FLAGS } from '../../../../../railwayFeatureFlags';
import { isConsultant as isConsultantCheck } from '../../../../../utils/helpers';
import SelectField from '../../../../hook_form_fields/SelectField';
import MilestoneWarning from '../../../../ProjectDetails/MilestoneWarning';
import AmountInput from '../../../../pricing-calculator/AmountInput';
import { SolarEconomics } from '../NewBorrowerForm';
import NewBorrowerFormFieldset from '../NewBorrowerFormFieldset';
import { InitialValues, timelineEstimations } from '../schema';
const { BorrowerForm } = Installer.UI;

type LoanInformationProps = {
  control: Control<InitialValues & SolarEconomics>;
  setValue: UseFormSetValue<InitialValues & SolarEconomics>;
  trigger?: UseFormTrigger<InitialValues & SolarEconomics>;
};

export const LoanInformation: FC<LoanInformationProps> = ({
  control,
  setValue,
  trigger,
}) => {
  const { account } = useAuth();

  const isConsultant = isConsultantCheck(account?.type);

  const { isActive } = useFeatureFlag();
  const [show20SpecialPayment, setShow20SpecialPayment] = useState(false);

  const specialPaymentOptions = useMemo(
    () =>
      show20SpecialPayment
        ? SPECIAL_PAYMENT_REQUEST_OPTIONS_WITH_20
        : SPECIAL_PAYMENT_REQUEST_OPTIONS,
    [show20SpecialPayment]
  );

  const installerHasFlex = Installer.hasFlex(account);

  const [
    loan_amount,
    loanType,
    loanTerm,
    loan_product,
    special_payment_percentage,
    net_funding,
    borrower_advance,
    interest_payer_during_construction,
  ] = useWatch({
    control,
    name: [
      'application.loan_amount',
      'loanType',
      'loanTerm',
      'application.loan_product',
      'application.special_payment_percentage',
      'application.net_funding',
      'application.borrower_advance',
      'application.interest_payer_during_construction',
    ],
  });

  const [activeAmountInput, setActiveAmountInput] = useState<
    'application.loan_amount' | 'application.net_funding'
  >('application.loan_amount');

  const projectAssets = useWatch({ control, name: 'projectAssets' });
  const selectedAssetTypes = useMemo(
    () => Asset.getSelectedTypes(projectAssets),
    [projectAssets]
  );

  // No assets or Flex-only assets: hide Loan Type and pin Flex.
  const hasOnlyFlexAssets = BorrowerForm.hasOnlyFlexAssets(selectedAssetTypes);
  const pinFlexLoanType = selectedAssetTypes.length === 0 || hasOnlyFlexAssets;

  // Milestone questions apply only when the installer holds an approved
  // Milestone program matching the project type (Flex program for a Flex
  // project, Solar program otherwise). Consultants always see them.
  const projectType = ProjectTypes.forAssetTypes(selectedAssetTypes);
  const hasApprovedProgramForProjectType =
    Installer.Programs.hasApprovedProgramForProjectType(
      account?.installer_programs,
      projectType
    );

  const setValueRevalidateOptions = {
    shouldValidate: true,
    shouldDirty: true,
    shouldTouch: true,
  };

  const numberLoanAmount = Number(loan_amount);

  const {
    productFamilyOptions,
    loanYearsOptions,
    interestRateOptions,
    selectedProductIsPremium,
    selectedInterestRate,
    dealerFee,
    ratesLoaded,
  } = useInstallerLoanProducts({
    loanType: loanType as ProductFamily,
    loanTerm,
    selectedProductId: loan_product,
    selectedAssetTypes,
  });

  const isFlexFourYearTerm =
    loanType === ProductFamily.FLEX &&
    !!loanTerm &&
    parseInt(loanTerm.slice(0, 2), 10) === 4;

  const showPremiumProductWarning =
    selectedProductIsPremium && numberLoanAmount > 1000000;

  const handleLoanTypeChange = (e: ChangeEvent<HTMLInputElement>) => {
    const _loanType = e.target.value;

    if (loanType !== _loanType) {
      setValue('loanTerm', '');
      setValue('application.loan_product', '');
    }

    // If Special Payment is selected and its value is not coming from the Calculator, pre-select to 30%
    if (
      _loanType === ProductFamily.SPECIAL_PAYMENT &&
      !special_payment_percentage
    ) {
      setValue('application.special_payment_percentage', 30, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    } else if (_loanType === ProductFamily.TAX_CREDIT) {
      setValue('application.special_payment_percentage', 25, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    } // if not Special Payment nor Tax Credit or changed loan type to Standard, reset the value
    else if (
      _loanType &&
      !SpecialPaymentProducts.includes(_loanType as ProductFamily)
    ) {
      setValue('application.special_payment_percentage', undefined, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  };

  const borrowerAdvanceSelectedYes = useMemo(() => {
    return Boolean(borrower_advance && borrower_advance !== 'No');
  }, [borrower_advance]);
  const showConstructionInterestPayer =
    BorrowerForm.showsConstructionInterestPayer(selectedAssetTypes);
  const milestoneSectionVisible =
    hasApprovedProgramForProjectType || isConsultant;

  useLinkedLoanAmounts({
    loanAmount: loan_amount,
    netFunding: net_funding,
    dealerFee,
    activeAmountInput,
    loanAmountField: 'application.loan_amount',
    netFundingField: 'application.net_funding',
    setLoanAmount: (value) => {
      setValue('application.loan_amount', value, setValueRevalidateOptions);
      setValue('economics.loanAmount', value);
      trigger?.(['application.loan_amount', 'application.net_funding']);
    },
    setNetFunding: (value) => {
      setValue('application.net_funding', value, setValueRevalidateOptions);
      trigger?.(['application.loan_amount', 'application.net_funding']);
    },
    echoLoanAmount: (value) => {
      setValue('economics.loanAmount', value);
    },
  });

  useEffect(() => {
    // "No" clears the payer. Flex hides the question and answers Borrower.
    if (borrower_advance === 'No') {
      if (interest_payer_during_construction != null) {
        setValue('application.interest_payer_during_construction', undefined, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      }
      return;
    }
    if (
      milestoneSectionVisible &&
      borrowerAdvanceSelectedYes &&
      !showConstructionInterestPayer &&
      interest_payer_during_construction !== 'Borrower'
    ) {
      setValue('application.interest_payer_during_construction', 'Borrower', {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    }
  }, [
    borrower_advance,
    borrowerAdvanceSelectedYes,
    interest_payer_during_construction,
    milestoneSectionVisible,
    setValue,
    showConstructionInterestPayer,
  ]);

  // Pin Flex when nothing is selected or the assets are Flex-only. Clear a
  // stale Flex value once the user picks Solar, storage-only, or EV Chargers.
  useEffect(() => {
    if (pinFlexLoanType) {
      if (loanType !== ProductFamily.FLEX) {
        setValue('loanType', ProductFamily.FLEX, setValueRevalidateOptions);
        setValue('loanTerm', '');
        setValue('application.loan_product', '');
      }
    } else if (loanType === ProductFamily.FLEX) {
      setValue('loanType', null, setValueRevalidateOptions);
      setValue('loanTerm', '');
      setValue('application.loan_product', '');
    }
    // setValueRevalidateOptions is a stable inline literal; intentionally
    // omitted from deps to avoid an exhaustive-deps churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinFlexLoanType, loanType, setValue]);

  useEffect(() => {
    if (
      loanTerm &&
      ratesLoaded &&
      !loanYearsOptions.some((obj) => obj.value === loanTerm)
    ) {
      setValue('loanTerm', '');
      setValue('application.loan_product', '');
    }
  }, [loanTerm, loanYearsOptions, setValue, ratesLoaded]);

  useEffect(() => {
    if (
      loan_product &&
      ratesLoaded &&
      !interestRateOptions.some((opt) => opt.value === loan_product)
    ) {
      setValue('application.loan_product', '');
    }
  }, [loan_product, interestRateOptions, setValue, ratesLoaded]);

  useEffect(() => {
    isActive(RAILWAY_FEATURE_FLAGS.specialPayment20, 'Railway').then(
      setShow20SpecialPayment
    );
  }, []);

  return (
    <NewBorrowerFormFieldset title={'Loan Information'}>
      <Grid container maxWidth="md" spacing={2}>
        {!pinFlexLoanType && (
          <Grid item xs={12} md={4}>
            <SelectField
              control={control}
              name={'loanType' as const}
              fullWidth
              label={'Loan Type'}
              tooltip={
                <>
                  <b>Standard Loans</b> have fixed terms and interest rates,
                  while <b>Special Payment Loans</b> have lower monthly payments
                  and a special payment on the 18th month allowing the borrower
                  to leverage tax credits.
                </>
              }
              choices={productFamilyOptions}
              onChange={handleLoanTypeChange}
            />
          </Grid>
        )}
        <Grid item xs={12} md={pinFlexLoanType ? 6 : 4}>
          <SelectField
            control={control}
            name={'loanTerm' as const}
            fullWidth
            label={'Loan Term / Amortization'}
            choices={loanYearsOptions.map((obj) => ({
              label: obj.label,
              value: obj.value,
            }))}
          />
          {isFlexFourYearTerm && (
            <WarningMessage
              warningMessage={FOUR_YEAR_TERM_WARNING_MESSAGE}
              sx={{ ml: 0, mt: 0 }}
              boxSx={{ mt: 1 }}
            />
          )}
        </Grid>
        <Grid item xs={12} md={pinFlexLoanType ? 6 : 4}>
          <SelectField
            control={control}
            name={'application.loan_product'}
            fullWidth
            label={'Interest Rate'}
            //User should select loan type and term before selecting interest rate
            disabled={
              interestRateOptions.length === 0 || !loanTerm || !loanType
            }
            choices={interestRateOptions}
          />
        </Grid>
        {SpecialPaymentProducts.includes(loanType as ProductFamily) && (
          <Grid item xs={12} md={12}>
            <SelectField
              control={control}
              name={'application.special_payment_percentage' as const}
              sx={{
                width: '100%',
              }}
              containerSx={{ width: 0.32 }}
              label={'Special Payment Request'}
              choices={
                loanType === ProductFamily.TAX_CREDIT
                  ? TAX_CREDIT_REQUEST_OPTIONS
                  : specialPaymentOptions
              }
            />
          </Grid>
        )}
        {PrepaymentPenalty.showsNote({
          selectedAssetTypes,
          loanTerm,
          interestRate: selectedInterestRate,
        }) && (
          <Grid item xs={12}>
            <MilestoneWarning
              type="error"
              warningMessage={PrepaymentPenalty.NOTE}
              sx={{ mb: 0 }}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={6}>
          <Box
            display={'flex'}
            flexDirection="column"
            justifyContent={'space-between'}
            height={'100%'}
          >
            <AmountInput
              control={control}
              name="application.net_funding"
              data-testid="netFunding"
              label="Net Funding"
              activeAmountInput={activeAmountInput}
              setActiveAmountInput={setActiveAmountInput}
              startAdornmentText="$"
            />
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <AmountInput
            control={control}
            name="application.loan_amount"
            data-testid="loanAmount"
            label="Loan Amount"
            activeAmountInput={activeAmountInput}
            setActiveAmountInput={setActiveAmountInput}
            startAdornmentText="$"
            showError={true}
            warningText={
              showPremiumProductWarning ? (
                <Typography sx={{ fontSize: 12 }} color={'error'}>
                  Loan amount must be less than $1,000,000 for Scenario 4 loans
                </Typography>
              ) : undefined
            }
          />
        </Grid>
        {milestoneSectionVisible && (
          <Grid item xs={12} sm={6.1}>
            <SelectField
              control={control}
              name={'application.borrower_advance'}
              fullWidth
              label={'Would you like to make this loan a Milestone Loan?'}
              tooltip={
                <Typography textAlign={'left'}>
                  Selecting &quot;Yes&quot; will enable Milestone payments for
                  this loan. Please note that the designated EPC/installer will
                  need to apply and be approved for the Sunstone Milestone
                  program before the loan can be finalized. If the installer and
                  Borrower are approved, the Borrower will receive a loan
                  agreement that includes the milestones as well as the
                  Milestone Program interest rate and must sign this agreement
                  to enable the program.
                </Typography>
              }
              required
              choices={[
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
              ]}
            />
          </Grid>
        )}
        <ShowIf
          condition={
            milestoneSectionVisible &&
            borrowerAdvanceSelectedYes &&
            showConstructionInterestPayer
          }
        >
          <Grid item xs={12} sm={6.1}>
            <SelectField
              control={control}
              name={'application.interest_payer_during_construction'}
              fullWidth
              label={
                'Who will pay the interest only payments during the construction period?'
              }
              choices={[...Milestone.interestPayerChoices]}
              required
              tooltip={
                <Typography textAlign={'left'}>
                  For all Milestone Loans, interest-only payments will apply to
                  any funds drawn prior to Substantial Completion. Installers
                  have the option to cover these payments. Please indicate below
                  whether these payments will be made by the installer or the
                  borrower. For the most up-to-date milestone interest-only
                  rate, please contact your Account Manager.
                </Typography>
              }
            />
          </Grid>
        </ShowIf>
        <ShowIf
          condition={
            BorrowerForm.hasOnlyStorageAssets(selectedAssetTypes) &&
            !installerHasFlex
          }
        >
          <Grid item xs={12} md={6} mt={2}>
            <SelectField
              fullWidth
              control={control}
              name="application.sys_prop_address_state"
              label="Property's State where the system will be installed"
              required
              choices={UNITED_STATES}
            />
          </Grid>
          <Grid item xs={12} md={6} mt={{ xs: 0, md: 2 }}>
            <SelectField
              fullWidth
              control={control}
              name="application.project_timeline_estimation"
              label="Project Timeline Estimation"
              required
              choices={timelineEstimations}
              tooltip="Please indicate your best estimate at this time, it is ok if this information changes later."
            />
          </Grid>
        </ShowIf>
      </Grid>
    </NewBorrowerFormFieldset>
  );
};

export default LoanInformation;
